import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import PageHeader from '../components/PageHeader';

export default function ExpressKurer() {
  return (
    <>
      <SEO
        title="Express, Kurér og Pakker | Fastlane ApS"
        description="Vi tilbyder, at sende dine pakker med det kurérfirma, der bedst passer til dit behov. Vi tilbyder pakkeforsendelser i stort set hele verden."
        canonicalUrl="https://fastlane.dk/eksprespakke"
      />

      <PageHeader
        title="Express, Kurér og Pakker"
        subtitle="Hurtige og sikre pakke- og kurerforsendelser i Europa og oversøisk – dag-til-dag og tidsleveringer."
        breadcrumbs={[
          { label: 'Ydelser', path: '/godstransport-i-europa' },
          { label: 'Express, Kurér og Pakker' }
        ]}
      />

      <div className="py-12 sm:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          {/* Main intro */}
          <div className="space-y-4 text-slate-700 leading-relaxed text-base sm:text-lg">
            <p>
              Fastlane kan via vort netværk af transportører tilbyde at sende pakker og mindre forsendelser i det meste af verdenen. Skal man sende eller modtage gods til eller fra 3. land altså lande uden for EU så skal man være opmærksom på, at der typisk skal laves en udførsel i afsender landet og en fortoldning i modtager landet.
            </p>
            <p className="font-semibold text-slate-900">
              Bemærk venligst Norge, UK og Schweiz er ikke med i toldunionen EU.
            </p>
            <p>
              Når der sendes pakker i et kurér system er udførsel og fortoldning typisk med, men vi skal bruge en præcis faktura og evt. en pakkeliste.
            </p>

            <div className="pt-2 flex flex-col sm:flex-row gap-3 sm:gap-4 w-full sm:w-auto">
              <a
                href="https://assets.auxodev.dk/assets/2e6d0d1c-821d-400c-80aa-275c6c0c86cc/3-3-16-rev-00-hvordan-jeg-f-r-den-rigtige-fragtpris.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full sm:w-auto inline-flex items-center justify-center text-xs font-semibold text-sky-800 bg-sky-50 hover:bg-sky-100 border border-sky-200 px-3.5 py-2.5 rounded-lg transition-colors gap-2 text-center"
              >
                <i className="fa-solid fa-file-pdf text-red-600 text-sm" aria-hidden="true" />
                <span>Hvordan får jeg den rigtige fragtpris? (PDF)</span>
              </a>

              <Link
                to="/ekspres-kurer-i-direkte-lastbiler"
                className="w-full sm:w-auto inline-flex items-center justify-center text-xs font-semibold text-[#0f2b48] bg-slate-100 hover:bg-slate-200 px-3.5 py-2.5 rounded-lg transition-colors text-center"
              >
                <span>Vi tilbyder også Direkte Biler (ekspresservice)</span>
                <i className="fa-solid fa-arrow-right ml-1.5" aria-hidden="true" />
              </Link>
            </div>
          </div>

          {/* Landeliste */}
          <div className="bg-slate-50 p-6 sm:p-8 rounded-2xl border border-slate-200/90 space-y-4">
            <h3 className="text-lg font-bold text-[#0f2b48]">
              I Europa sender og henter vi pakkegods til og fra:
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 text-sm text-slate-700">
              {[
                'Tyskland',
                'Benelux (Holland, Belgien, Lux.)',
                'Frankrig',
                'Italien',
                'Malta',
                'Cypern',
                'Spanien / Portugal',
                'Danmark',
                'Finland',
                'Sverige',
                'Grækenland',
                'UK (England) / Irland',
                'Baltikum (Estland, Letland, Litauen)',
                'Bulgarien',
                'Kroatien',
                'Polen',
                'Rumænien',
                'Slovenien',
                'Slovakiet',
                'Tjekkiet',
                'Ungarn',
                'Østrig'
              ].map((c, i) => (
                <div key={i} className="flex items-center gap-1.5 text-xs sm:text-sm">
                  <i className="fa-solid fa-check text-sky-600 text-xs" aria-hidden="true" />
                  <span>{c}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Fordelene ved kurérløsninger */}
          <div className="space-y-4 text-slate-700 leading-relaxed text-sm sm:text-base border-t border-slate-200 pt-8">
            <h3 className="text-xl font-bold text-[#0f2b48]">Fordelene ved kurérløsninger:</h3>
            <ul className="list-disc pl-5 space-y-1.5 text-slate-700">
              <li>det går hurtigt - ekspresforsendelse</li>
              <li>det billigt for små pakker</li>
              <li>det er fra afsenders dør og helt frem til modtagers dør</li>
              <li>fortoldning og udførsel er en del af servicen - relevant ved 3. land, d.v.s. lande uden for EU f.eks. Schweiz, UK og Norge.</li>
              <li>normalt er der fuld track & trace på entydigt sendingsnummer</li>
              <li>kurér er i nogle tilfælde konkurrencedygtigt til sammenligning med luftfragt</li>
            </ul>
          </div>

          {/* Typiske Kurérservices */}
          <div className="space-y-4 text-slate-700 leading-relaxed text-sm sm:text-base border-t border-slate-200 pt-8">
            <h3 className="text-xl font-bold text-[#0f2b48]">Typiske Kurérservices</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
                <i className="fa-solid fa-clock text-sky-700 mb-2 block" aria-hidden="true" />
                <h4 className="font-bold text-sm text-slate-900 mb-1">Dag til dag</h4>
                <p className="text-xs text-slate-600">Express-forsendelse af dokumenter og pakker - dag til dag</p>
              </div>
              <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
                <i className="fa-solid fa-sun text-amber-600 mb-2 block" aria-hidden="true" />
                <h4 className="font-bold text-sm text-slate-900 mb-1">Før kl. 9.00</h4>
                <p className="text-xs text-slate-600">Express-forsendelse - levering før kl. 9.00</p>
              </div>
              <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
                <i className="fa-solid fa-business-time text-sky-700 mb-2 block" aria-hidden="true" />
                <h4 className="font-bold text-sm text-slate-900 mb-1">Før kl. 12.00</h4>
                <p className="text-xs text-slate-600">Express-forsendelser - levering før kl. 12.00</p>
              </div>
            </div>
          </div>

          {/* Hvorfor outsource kurér via Fastlane */}
          <div className="space-y-4 text-slate-700 leading-relaxed text-sm sm:text-base border-t border-slate-200 pt-8">
            <h3 className="text-xl font-bold text-[#0f2b48]">
              Hvorfor skal vi sende dine pakker / dokumenter med kurér? Altså du kan jo bare selv bestille direkte ved en af de store operatører?
            </h3>
            <ul className="list-disc pl-5 space-y-2 text-slate-700 text-sm">
              <li>Vi vurderer alle sendinger i forhold til hvilken transporttype (skib, fly, vej el. kurér) der er den rigtige.</li>
              <li>Er en kurérløsning optimal vurderer vi igen, hvilket kurérfirma, der er optimalt.</li>
              <li>Vi booker sendingen på den rigtige måde via det valgte kurérfirmas booking system.</li>
              <li>Vi følger op på sendingen via track and trace og slipper den først når den er leveret.</li>
              <li>Med andre ord kan du trygt outsource dine kurérforsendelser via os.</li>
              <li>Typisk vil du kunne spare penge fordi vi har bedre fragtrater end du selv kan opnå.</li>
              <li>Vi har generelt gode fragtpriser.</li>
            </ul>

            <p className="text-xs text-slate-500 pt-2 leading-relaxed">
              Præcis beskrivelse af hvert enkelt kolli er vigtig p.g.a pladsbegrænsning afhængig af kurérfirma og geografiske områder. Som minimum skal vi kende godstype, dimensioner L x B x H, vægt m.m.
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
